/**
 * The rental terms as text (CTR-1): what the loader's files, the Owner's
 * editor (Regole del contratto, OPS-1) and the contract's renderers agree on.
 * Pure: strings in, strings out.
 */

/**
 * C0 and C1 control characters, and the bidirectional overrides that can hide
 * or reorder text on a screen or a PDF. A newline is a character of its own:
 * allowed where text runs over several lines, refused on one line.
 */
export function hasHiddenCharacters(text: string, multiline: boolean): boolean {
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0
    if (multiline && code === 0x0a) continue
    if (code < 0x20 || (code >= 0x7f && code < 0xa0)) return true
    if (code === 0x200e || code === 0x200f) return true
    if (code >= 0x202a && code <= 0x202e) return true
    if (code >= 0x2066 && code <= 0x2069) return true
  }
  return false
}

/**
 * One paragraph of the terms. The clauses are numbered in the text itself
 * ("1. …"); a paragraph that opens with no number (a preamble, a closing
 * line) keeps its words as they are and takes no number.
 */
export interface Clause {
  readonly numbered: boolean
  readonly text: string
}

/** The number a numbered paragraph opens with: up to three digits, a dot, a space. */
const NUMBERED = /^(\d{1,3})\.\s+/

/**
 * The body split into its paragraphs, as every renderer splits it (a blank
 * line between two), each numbered one stripped of its number. The editor
 * shows these; `bodyOf` writes them back.
 */
export function clausesOf(body: string): Clause[] {
  return body
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0)
    .map((paragraph) => {
      const match = NUMBERED.exec(paragraph)
      return match
        ? { numbered: true, text: paragraph.slice(match[0].length) }
        : { numbered: false, text: paragraph }
    })
}

/**
 * The paragraphs joined back into a body: the numbered ones take their place
 * in the list as their number (1, 2, 3 … in order), the others stand as they
 * are; a blank line between two; empty paragraphs are left out. For a text
 * whose numbers are already in order this gives the text back byte for byte.
 */
export function bodyOf(clauses: readonly Clause[]): string {
  let number = 0
  return clauses
    .map((clause) => clause.text.trim())
    .map((text, index) => {
      if (text.length === 0) return ''
      return clauses[index]?.numbered ? `${++number}. ${text}` : text
    })
    .filter((paragraph) => paragraph.length > 0)
    .join('\n\n')
}

/**
 * The clause numbers a specific-approval paragraph cites, as the lawyer writes
 * them ("n. 1 (…), n. 8 (…)"), each once, in the order they appear. After a
 * clause is moved or removed these name other clauses: the editor lists them
 * against the clauses' new numbers and never rewrites the paragraph.
 */
export function citedClauses(approval: string): number[] {
  const cited: number[] = []
  for (const match of approval.matchAll(/\bn\.\s*(\d{1,3})\b/g)) {
    const number = Number(match[1])
    if (!cited.includes(number)) cited.push(number)
  }
  return cited
}
